import { message } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  sharingCallsFor,
  useProjectShareEmails,
  type SharableAgentType,
} from 'features/trend-detection/components/agent-sharing-calls'
import { useTrendDetectionService } from 'features/trend-detection/service/hook'
import type { AgentCollaboratorsResponse } from 'features/trend-detection/types'

export interface UseAgentSharingParams {
  projectId: string
  agentUuid: string
  agentType: SharableAgentType
}

export interface UseAgentSharingResult {
  collaborators: AgentCollaboratorsResponse | null
  isLoading: boolean
  isAdding: boolean
  availableEmails: string[]
  add: (email: string) => Promise<void>
  remove: (email: string) => Promise<void>
}

/** State + calls behind `AgentSharing` — fetches on mount. Whether this is
 * mounted at all (inline in the edit form, or inside `ShareResourceModal`,
 * which unmounts its children while closed) is the caller's concern. */
export function useAgentSharing({
  projectId,
  agentUuid,
  agentType,
}: UseAgentSharingParams): UseAgentSharingResult {
  // bully ignore[no-service-hooks-outside-containers: this hook instantiates the service hook directly, the same shape as the AgentSharingModal/AgentSharingSection code it replaces and as ask-yogi's use-shareable-users.ts/use-prompt-shares.ts for the sibling sharing feature — not threaded through Container; follow-up: revisit if this rule's granularity is extended to leaf-level query hooks., view-service-hooks: same site as the ignore above — every file under components/ counts as a View to this rule too. follow-up: revisit if this rule's granularity is extended to leaf-level query hooks.]
  const service = useTrendDetectionService()
  const allEmails = useProjectShareEmails(projectId)
  const [collaborators, setCollaborators] =
    useState<AgentCollaboratorsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAdding, setIsAdding] = useState(false)
  const calls = useMemo(
    () => sharingCallsFor(service, agentType),
    [service, agentType],
  )

  const fetchCollaborators = useCallback(async () => {
    setIsLoading(true)
    try {
      setCollaborators(await calls.fetch(projectId, agentUuid))
    } catch {
      // Silently fail — collaborators will show as empty
    } finally {
      setIsLoading(false)
    }
  }, [calls, projectId, agentUuid])

  useEffect(() => {
    fetchCollaborators()
  }, [fetchCollaborators])

  const taken = new Set(
    [...(collaborators?.editors ?? []), ...(collaborators?.viewers ?? [])].map(
      (c) => c.email,
    ),
  )
  const availableEmails = allEmails.filter((email) => !taken.has(email))

  const add = useCallback(
    async (email: string): Promise<void> => {
      setIsAdding(true)
      try {
        setCollaborators(await calls.share(projectId, agentUuid, [email]))
        message.success(`Shared with ${email}`)
      } catch {
        message.error('Failed to share agent')
      } finally {
        setIsAdding(false)
      }
    },
    [calls, projectId, agentUuid],
  )

  const remove = useCallback(
    async (email: string): Promise<void> => {
      try {
        setCollaborators(await calls.unshare(projectId, agentUuid, [email]))
        message.success('User removed')
      } catch {
        message.error('Failed to remove user')
      }
    },
    [calls, projectId, agentUuid],
  )

  return { collaborators, isLoading, isAdding, availableEmails, add, remove }
}
